import React, { useState } from "react";
import { View, TouchableOpacity, Text, StyleSheet, Modal } from "react-native";
import {
  Download,
  Pen,
  Copy,
  RotateCcw,
  FileText,
  MoreHorizontal,
  Edit,
  Trash2,
} from "lucide-react";
import { NoteActionsProps } from "../../types/VoiceNotes";
import CopyFieldsModal from "../modals/CopyFieldsModal";

const NoteActions: React.FC<NoteActionsProps> = ({
  note,
  editingNoteId,
  signedNotes,
  reprocessingNoteId,
  onDownloadSummary,
  onDownloadSignedPDF,
  onSignDocument,
  onDeleteNote,
  onEditSummary,
  onDownloadTranscript,
  onDownloadAudio,
  onReprocessNote,
}) => {
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showCopyFields, setShowCopyFields] = useState(false);

  const isReprocessingThisNote = reprocessingNoteId === note.id;
  const isEditingThisNote = editingNoteId === note.id;
  const isSigned = Boolean(note.is_signed || signedNotes[note.id]?.isSigned);

  const closeOptionsThen = (action: () => void) => {
    setShowOptionsModal(false);
    action();
  };

  return (
    <>
      <View style={styles.summaryActions}>
        <TouchableOpacity
          style={styles.primaryActionButton}
          onPress={() =>
            isSigned ? onDownloadSignedPDF(note) : onDownloadSummary(note)
          }
        >
          <Download size={16} color="#0085FF" />
          <Text style={styles.primaryActionButtonText}>
            {isSigned ? "Signed PDF" : "PDF"}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.primaryActionButton, styles.optionsButton]}
          onPress={() => setShowOptionsModal(true)}
        >
          <MoreHorizontal size={16} color="#0085FF" />
          <Text style={styles.primaryActionButtonText}>Options</Text>
        </TouchableOpacity>
      </View>

      <Modal
        visible={showOptionsModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowOptionsModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Note Options</Text>
            <Text style={styles.modalSubtitle}>
              Choose an action for this note.
            </Text>

            <View style={styles.optionList}>
              <TouchableOpacity
                style={[
                  styles.optionButton,
                  (!note.audioId || isReprocessingThisNote) &&
                    styles.disabledButton,
                ]}
                onPress={() => closeOptionsThen(() => onReprocessNote(note))}
                disabled={!note.audioId || isReprocessingThisNote}
              >
                <RotateCcw size={16} color="#0085FF" />
                <Text style={styles.optionButtonText}>
                  {isReprocessingThisNote ? "Reprocessing..." : "Reprocess"}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.optionButton,
                  isEditingThisNote && styles.disabledButton,
                ]}
                onPress={() => closeOptionsThen(() => onSignDocument(note))}
                disabled={isEditingThisNote}
              >
                <Pen
                  size={16}
                  color={isEditingThisNote ? "#CCCCCC" : "#0085FF"}
                />
                <Text
                  style={[
                    styles.optionButtonText,
                    isEditingThisNote ? styles.disabledText : null,
                  ]}
                >
                  {isSigned ? "Sign Again" : "Sign"}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.optionButton,
                  !note.note_json && styles.disabledButton,
                ]}
                onPress={() => {
                  setShowOptionsModal(false);
                  setShowCopyFields(true);
                }}
                disabled={!note.note_json}
              >
                <Copy
                  size={16}
                  color={!note.note_json ? "#CCCCCC" : "#0085FF"}
                />
                <Text
                  style={[
                    styles.optionButtonText,
                    !note.note_json ? styles.disabledText : null,
                  ]}
                >
                  Copy
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.optionButton, isSigned && styles.disabledButton]}
                onPress={() => closeOptionsThen(() => onEditSummary(note))}
                disabled={isSigned}
              >
                <Edit size={16} color={isSigned ? "#CCCCCC" : "#0085FF"} />
                <Text
                  style={[
                    styles.optionButtonText,
                    isSigned ? styles.disabledText : null,
                  ]}
                >
                  Edit
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.optionButton,
                  !note.transcription && styles.disabledButton,
                ]}
                onPress={() =>
                  closeOptionsThen(() => onDownloadTranscript(note))
                }
                disabled={!note.transcription}
              >
                <FileText
                  size={16}
                  color={!note.transcription ? "#CCCCCC" : "#0085FF"}
                />
                <Text
                  style={[
                    styles.optionButtonText,
                    !note.transcription ? styles.disabledText : null,
                  ]}
                >
                  Transcript
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.optionButton,
                  !note.url && styles.disabledButton,
                ]}
                onPress={() => closeOptionsThen(() => onDownloadAudio(note))}
                disabled={!note.url}
              >
                <Download size={16} color={!note.url ? "#CCCCCC" : "#0085FF"} />
                <Text
                  style={[
                    styles.optionButtonText,
                    !note.url ? styles.disabledText : null,
                  ]}
                >
                  Audio
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.optionButton, styles.optionButtonDanger]}
                onPress={() => closeOptionsThen(() => onDeleteNote(note))}
              >
                <Trash2 size={16} color="#E53935" />
                <Text
                  style={[
                    styles.optionButtonText,
                    styles.optionButtonDangerText,
                  ]}
                >
                  Delete
                </Text>
              </TouchableOpacity>
            </View>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowOptionsModal(false)}
              >
                <Text style={styles.cancelButtonText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <CopyFieldsModal
        visible={showCopyFields}
        onClose={() => setShowCopyFields(false)}
        noteJson={note.note_json}
        noteId={note.id}
        noteMarkdown={note.note}
      />
    </>
  );
};

const styles = StyleSheet.create({
  summaryActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  primaryActionButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    backgroundColor: "#F7FBFF",
    borderWidth: 1,
    borderColor: "#D6E9FF",
  },
  primaryActionButtonText: {
    fontSize: 14,
    color: "#0085FF",
    marginLeft: 4,
    fontWeight: "500",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    width: "100%",
    maxWidth: 420,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#333333",
    marginBottom: 8,
  },
  modalSubtitle: {
    fontSize: 16,
    color: "#666666",
    marginBottom: 20,
  },
  optionList: {
    marginBottom: 4,
  },
  optionButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F7FBFF",
    borderWidth: 1,
    borderColor: "#D6E9FF",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  optionsButton: {
    marginLeft: 8,
  },
  optionButtonText: {
    fontSize: 14,
    color: "#0085FF",
    marginLeft: 8,
    fontWeight: "500",
  },
  optionButtonDanger: {
    backgroundColor: "#FFF2F0",
    borderColor: "#F8C5C2",
  },
  optionButtonDangerText: {
    color: "#E53935",
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
  },
  cancelButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F2F2F2",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  cancelButtonText: {
    fontSize: 14,
    color: "#555555",
  },
  disabledButton: {
    opacity: 0.6,
  },
  disabledText: {
    color: "#CCCCCC",
  },
});

export default NoteActions;
